import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Sparkles, Loader2, Package, ShoppingBag, Pencil, PenLine, Star, Upload as UploadIcon, Clock, CheckCircle2, XCircle, Building2, CopyPlus, Archive as ArchiveIcon, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { StarRating } from "@/components/StarRating";
import { api, downloadSkillZip, apiErr } from "@/lib/api";
import { UseInClaudeApps } from "@/components/UseInClaudeApps";
import { purchaseOrDownload } from "@/lib/payments";
import { useAuth } from "@/context/AuthContext";

// The publish toggle's replacement wherever public marketplace publishing isn't the lane
// (2026-07-13): org artifacts show their state; org members get share-to-org instead.
function PublishOrShareRow({ skill, org, sharing, onTogglePublic, onShare }) {
  const approved = skill.source !== "upload" || skill.review_status === "approved";
  if (skill.org_id) {
    return (
      <div className="mt-3 flex items-center gap-1.5 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] px-3 py-2 text-xs text-[color:var(--rb-text-faint)]">
        <Building2 className="h-3.5 w-3.5" /> In your org's catalog — edits go back through review
      </div>
    );
  }
  if (org) {
    return (
      <>
        {/* Personal lane (2026-07-14): an org member's own approved gallery artifact syncs to
            their machines via the plugin — no org publish needed. */}
        {(skill.review_status || "approved") === "approved" && (
          <div className="mt-3 rounded-xl border border-[color:rgba(45,212,191,0.3)] bg-[color:rgba(45,212,191,0.08)] px-3 py-2 text-xs text-[color:var(--rb-accent)]" data-testid="personal-sync-note">
            Private — syncs to your machine via the Runebox plugin
          </div>
        )}
        <Button size="sm" variant="outline" onClick={() => onShare(skill)} disabled={sharing} data-testid="share-to-org-button"
          className="mt-3 w-full rounded-xl border-[color:rgba(212,175,55,0.4)] bg-[color:var(--rb-gold-soft)] text-xs text-[color:var(--rb-gold)] hover:bg-[color:rgba(212,175,55,0.18)] disabled:opacity-60">
          {sharing ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Sharing…</> : <><Building2 className="mr-1.5 h-3.5 w-3.5" /> Share with {org.name}</>}
        </Button>
      </>
    );
  }
  if (skill.forked_from) {
    return (
      <div className="mt-3 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] px-3 py-2 text-xs text-[color:var(--rb-text-faint)]">
        Private copy of a purchased skill — yours to edit, not to resell
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-center justify-between rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] px-3 py-2 text-xs">
      <span className="text-[color:var(--rb-text-faint)]">
        {approved ? (skill.is_public ? "Public on marketplace" : "Ready to publish") : "Publishing locked until approved"}
      </span>
      <Switch checked={!!skill.is_public} disabled={!approved} onCheckedChange={(v) => onTogglePublic(skill, v)} data-testid="publish-toggle" />
    </div>
  );
}

function EditDialog({ skill, open, onOpenChange, onSaved }) {
  const { org } = useAuth(); // org members neither sell publicly nor price anything (2026-07-14)
  const initForm = (s) => ({
    name: s?.name || "",
    short_description: s?.short_description || "",
    long_description: s?.long_description || "",
    category: s?.category || "",
    price_credits: s?.price_cents != null ? String(s.price_cents / 100) : "1",
  });
  const [form, setForm] = useState(() => initForm(skill));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initForm(skill)); }, [skill]);

  const save = async () => {
    setSaving(true);
    try {
      const { price_credits, ...rest } = form;
      const price_cents = Math.max(0, Math.round((parseFloat(price_credits) || 0) * 100));
      await api.patch(`/skills/${skill.id}`, org ? rest : { ...rest, price_cents });
      toast.success("Saved");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErr(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]">
        <DialogHeader><DialogTitle className="text-[color:var(--rb-text)]">Info — name, description &amp; listing details</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[color:var(--rb-text-faint)]">Display name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
          </div>
          <div>
            <label className="text-xs text-[color:var(--rb-text-faint)]">Category</label>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="edit-category-input" className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
          </div>
          <div>
            <label className="text-xs text-[color:var(--rb-text-faint)]">Short description</label>
            <Input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
          </div>
          <div>
            <label className="text-xs text-[color:var(--rb-text-faint)]">Long description</label>
            <Textarea value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} className="mt-1 min-h-[90px] border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
          </div>
          {!org && (
            <div>
              <label className="text-xs text-[color:var(--rb-text-faint)]">Price to buyers (credits) — 0 is free</label>
              <Input type="number" min="0" max="1000" step="1" value={form.price_credits}
                onChange={(e) => setForm({ ...form, price_credits: e.target.value })}
                data-testid="edit-price-input"
                className="mt-1 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)]" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-skill-edit-button" className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewStatusBadge({ status }) {
  const map = {
    approved: { cls: "bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]", icon: CheckCircle2, label: "Approved" },
    pending: { cls: "bg-[color:var(--rb-gold-soft)] text-[color:var(--rb-gold)]", icon: Clock, label: "Pending review" },
    appealed: { cls: "bg-[color:var(--rb-gold-soft)] text-[color:var(--rb-gold)]", icon: Clock, label: "Appealed — in review" },
    rejected: { cls: "bg-[color:rgba(248,113,113,0.14)] text-[color:#f87171]", icon: XCircle, label: "Rejected" },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return <Badge className={`gap-1 ${s.cls}`} data-testid="upload-review-status"><Icon className="h-3 w-3" /> {s.label}</Badge>;
}

// Whole card navigates to the view page (2026-07-14) — clicks on real controls don't.
const cardClickGuard = (open) => (e) => {
  if (e.target.closest("button, a, input, textarea, [role='switch'], label")) return;
  open();
};
const CARD_CLICKABLE = "cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:border-[color:var(--rb-accent)]";

function UploadedCard({ skill, org, sharing, onDownload, onTogglePublic, onShare, onEdit, onEditContent, onView, onAppeal, onArchive }) {
  const reasons = skill.scan?.reasons || [];
  const [appealMsg, setAppealMsg] = useState("");
  // One appeal per upload, gallery only — org artifacts are their org admin's call.
  const canAppeal = skill.review_status === "rejected" && !skill.org_id && !skill.appeal;
  return (
    <div onClick={cardClickGuard(() => onView(skill))}
      className={`flex flex-col rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5 ${CARD_CLICKABLE}`} data-testid="uploaded-skill-card">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">{skill.category || "General"}</Badge>
        <ReviewStatusBadge status={skill.review_status} />
      </div>
      <h3 className="rb-mono mt-3 truncate text-[15px] font-semibold text-[color:var(--rb-text)]">{skill.slug}</h3>
      <p className="mt-1.5 line-clamp-2 text-sm text-[color:var(--rb-text-dim)]">{skill.short_description || "—"}</p>

      {skill.review_status === "rejected" && (
        <div className="mt-3 rounded-xl border border-[color:rgba(248,113,113,0.3)] bg-[color:rgba(248,113,113,0.06)] p-2.5">
          <p className="text-xs font-medium text-[color:#f87171]">Flagged by safety scan:</p>
          <ul className="mt-1 list-inside list-disc text-[11px] text-[color:var(--rb-text-faint)]">
            {reasons.slice(0, 4).map((r, i) => <li key={i} className="truncate">{r}</li>)}
          </ul>
          {canAppeal && (
            <div className="mt-2">
              <Textarea value={appealMsg} onChange={(e) => setAppealMsg(e.target.value)} data-testid="appeal-message"
                placeholder="Think this is a mistake? Tell the review team why (optional)…"
                className="min-h-[56px] resize-y border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] text-xs text-[color:var(--rb-text)]" />
              <Button size="sm" variant="outline" onClick={() => onAppeal(skill, appealMsg)} data-testid="appeal-button"
                className="mt-2 w-full rounded-xl border-[color:var(--rb-border)] text-xs text-[color:var(--rb-text)]">
                Appeal to the review team
              </Button>
            </div>
          )}
        </div>
      )}

      <PublishOrShareRow skill={skill} org={org} sharing={sharing} onTogglePublic={onTogglePublic} onShare={onShare} />

      <div className="mt-4 flex items-center gap-2 border-t border-[color:var(--rb-border)] pt-3">
        <Button size="sm" variant="outline" onClick={() => onEdit(skill)} className="border-[color:var(--rb-border)]"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit info</Button>
        <Button size="sm" variant="outline" onClick={() => onEditContent(skill)} data-testid="edit-content-button" className="border-[color:var(--rb-border)]">
          <PenLine className="mr-1 h-3.5 w-3.5" /> Modify
        </Button>
        <div className="flex-1" />
        {!org && (
          <Button size="sm" onClick={() => onDownload(skill)} data-testid="uploaded-download-button" title="Download .zip"
            className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
            <Download className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => onArchive(skill)} data-testid="archive-button" title="Archive — unpublish and hide, never delete" className="border-[color:var(--rb-border)]">
          <ArchiveIcon className="mr-1 h-3.5 w-3.5" /> Archive
        </Button>
      </div>
      {/* Grab-for-Claude-apps path — shown for org members too (their download button is hidden,
          but the creator can always download their own skill, Phase 11). Skill kind only. */}
      {(skill.kind || "skill") === "skill" && (
        <UseInClaudeApps onDownload={() => onDownload(skill)} className="mt-2 h-9 w-full" />
      )}
    </div>
  );
}

function CreatedCard({ skill, org, sharing, onDownload, onTogglePublic, onShare, onEdit, onEditContent, onView, onArchive }) {
  return (
    <div onClick={cardClickGuard(() => onView(skill))}
      className={`flex flex-col rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5 ${CARD_CLICKABLE}`} data-testid="created-skill-card">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">{skill.category || "General"}</Badge>
        {skill.forked_from ? <Badge className="bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]">Copy</Badge> : null}
      </div>
      <h3 className="rb-mono mt-3 truncate text-[15px] font-semibold text-[color:var(--rb-text)]">{skill.slug}</h3>
      <p className="mt-1.5 line-clamp-2 text-sm text-[color:var(--rb-text-dim)]">{skill.short_description || "—"}</p>
      <div className="mt-4 flex items-center justify-between text-xs text-[color:var(--rb-text-faint)]">
        <StarRating rating={skill.avg_rating || 0} count={skill.review_count || 0} size={13} />
        <span className="flex items-center gap-1"><Download className="h-3.5 w-3.5" /> {skill.download_count || 0}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] p-2 text-center text-xs">
        <div><div className="text-[color:var(--rb-gold)]">${((skill.earnings_cents || 0) / 100).toFixed(2)}</div><div className="text-[color:var(--rb-text-faint)]">earned</div></div>
        <div><div className="text-[color:var(--rb-text)]">{skill.sales_count || 0}</div><div className="text-[color:var(--rb-text-faint)]">sales</div></div>
      </div>
      <PublishOrShareRow skill={skill} org={org} sharing={sharing} onTogglePublic={onTogglePublic} onShare={onShare} />
      <div className="mt-4 flex items-center gap-2 border-t border-[color:var(--rb-border)] pt-3">
        <Button size="sm" variant="outline" onClick={() => onEdit(skill)} data-testid="edit-skill-button" className="border-[color:var(--rb-border)]">
          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit info
        </Button>
        <Button size="sm" variant="outline" onClick={() => onEditContent(skill)} data-testid="edit-content-button" className="border-[color:var(--rb-border)]">
          <PenLine className="mr-1 h-3.5 w-3.5" /> Modify
        </Button>
        <div className="flex-1" />
        {!org && (
          <Button size="sm" onClick={() => onDownload(skill)} data-testid="download-skill-button" title="Download .zip"
            className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
            <Download className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => onArchive(skill)} data-testid="archive-button" title="Archive — unpublish and hide, never delete" className="border-[color:var(--rb-border)]">
          <ArchiveIcon className="mr-1 h-3.5 w-3.5" /> Archive
        </Button>
      </div>
      {/* Grab-for-Claude-apps path — shown for org members too (their download button is hidden,
          but the creator can always download their own skill, Phase 11). Skill kind only. */}
      {(skill.kind || "skill") === "skill" && (
        <UseInClaudeApps onDownload={() => onDownload(skill)} className="mt-2 h-9 w-full" />
      )}
    </div>
  );
}

function PurchasedCard({ skill, org, onDownload, onReview, onFork }) {
  return (
    <div onClick={cardClickGuard(() => onReview(skill))}
      className={`flex flex-col rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5 ${CARD_CLICKABLE}`} data-testid="purchased-skill-card">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">{skill.category || "General"}</Badge>
        {skill.my_review ? <Badge className="bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]">Reviewed</Badge> : null}
      </div>
      <h3 className="rb-mono mt-3 truncate text-[15px] font-semibold text-[color:var(--rb-text)]">{skill.slug}</h3>
      <p className="mt-1.5 line-clamp-2 text-sm text-[color:var(--rb-text-dim)]">{skill.short_description || "—"}</p>
      <div className="mt-4 flex items-center gap-1 text-xs text-[color:var(--rb-text-faint)]">
        <StarRating rating={skill.avg_rating || 0} count={skill.review_count || 0} size={13} />
      </div>
      {org && (
        <div className="mt-3 rounded-xl border border-[color:rgba(45,212,191,0.3)] bg-[color:rgba(45,212,191,0.08)] px-3 py-2 text-xs text-[color:var(--rb-accent)]">
          Syncs to your machine via the Runebox plugin
        </div>
      )}
      <div className="mt-4 flex gap-2 border-t border-[color:var(--rb-border)] pt-3">
        <Button size="sm" variant="outline" onClick={() => onReview(skill)} data-testid="rate-review-button" className="flex-1 border-[color:var(--rb-border)]">
          <Star className="mr-1.5 h-3.5 w-3.5" /> {skill.my_review ? "Edit review" : "Rate / Review"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onFork(skill)} data-testid="fork-button" className="border-[color:var(--rb-border)]">
          <CopyPlus className="mr-1 h-3.5 w-3.5" /> Copy & edit
        </Button>
        {!org && (
          <Button size="sm" onClick={() => onDownload(skill)} className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
            <Download className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function MySkills() {
  const navigate = useNavigate();
  const { org } = useAuth();
  const [created, setCreated] = useState([]);
  const [uploaded, setUploaded] = useState([]);
  const [purchased, setPurchased] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [sharingId, setSharingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([api.get("/skills/mine"), api.get("/skills/purchased")]);
      const mine = c.data || [];
      setCreated(mine.filter((s) => s.source !== "upload" && !s.archived));
      setUploaded(mine.filter((s) => s.source === "upload" && !s.archived));
      setArchived(mine.filter((s) => s.archived));
      setPurchased(p.data || []);
    } catch (e) {
      toast.error("Failed to load your skills");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadOrBuy = async (skill) => {
    try {
      const res = await purchaseOrDownload(skill.id, skill.slug);
      toast.success(res.action === "downloaded" ? "Downloaded" : "Purchased & downloaded");
      load();
    } catch (e) {
      if (e.code === "insufficient_credits") {
        toast.error("Not enough credits — top up your wallet.");
        navigate("/wallet");
      } else {
        const d = e.response?.data?.detail;
        toast.error((d && (d.message || d)) || e.message || "Failed");
      }
    }
  };

  const justDownload = async (skill) => {
    try {
      await downloadSkillZip(skill.id, skill.slug);
      toast.success("Downloaded");
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  const togglePublic = async (skill, value) => {
    try {
      await api.patch(`/skills/${skill.id}`, { is_public: value });
      toast.success(value ? "Published to marketplace" : "Unpublished");
      load();
    } catch (e) {
      toast.error(apiErr(e, "Update failed"));
    }
  };

  const appeal = async (skill, message) => {
    try {
      await api.post(`/skills/${skill.id}/appeal`, { message });
      toast.success("Appeal sent — the review team will take a look");
      load();
    } catch (e) {
      toast.error(apiErr(e, "Appeal failed"));
    }
  };

  // Share-to-org (2026-07-13): scans now; clean goes straight to the plugin, flagged queues.
  // The scan takes a beat, so guard against the impatient double-click that used to mint a
  // version per click (2026-07-14).
  const shareToOrg = async (skill) => {
    if (sharingId) return;
    setSharingId(skill.id);
    try {
      const { data } = await api.post(`/skills/${skill.id}/share-to-org`);
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Couldn't share to your org"));
    } finally {
      setSharingId(null);
    }
  };

  const fork = async (skill) => {
    try {
      const { data } = await api.post(`/skills/${skill.id}/fork`);
      toast.success(data.message || "Copied to My Artifacts");
      navigate(`/skill/${data.id}/edit`);
    } catch (e) {
      toast.error(apiErr(e, "Couldn't make a copy"));
    }
  };

  const viewDetail = (sk) => navigate(`/skill/${sk.id}`, { state: { from: "my-skills" } });
  const editContent = (sk) => navigate(`/skill/${sk.id}/edit`);

  const archive = async (sk) => {
    if (!window.confirm(`Archive ${sk.slug}? It's unpublished and hidden — unarchive any time.`)) return;
    try {
      await api.post(`/skills/${sk.id}/archive`);
      toast.success(`Archived ${sk.slug}`);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Couldn't archive"));
    }
  };
  const unarchive = async (sk) => {
    try {
      await api.post(`/skills/${sk.id}/unarchive`);
      toast.success(`Unarchived ${sk.slug} — publish or share it again when ready`);
      load();
    } catch (e) {
      toast.error(apiErr(e, "Couldn't unarchive"));
    }
  };

  return (
    <AppShell>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">My Artifacts</h1>
          <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">Your built, uploaded, and purchased skills, agents, and more.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/upload")} data-testid="upload-skill-cta" className="rounded-xl border-[color:var(--rb-border)] text-[color:var(--rb-text)]">
            <UploadIcon className="mr-1.5 h-4 w-4" /> Upload
          </Button>
          <Button onClick={() => navigate("/build")} data-testid="new-skill-cta" className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
            <Sparkles className="mr-1.5 h-4 w-4" /> New Skill
          </Button>
        </div>
      </div>

      <Tabs defaultValue="created">
        <TabsList className="bg-[color:var(--rb-surface)]">
          <TabsTrigger value="created" data-testid="tab-created" className="data-[state=active]:bg-[color:var(--rb-accent)] data-[state=active]:text-[color:var(--rb-void)]">
            <Package className="mr-1.5 h-3.5 w-3.5" /> Created ({created.length})
          </TabsTrigger>
          <TabsTrigger value="uploaded" data-testid="tab-uploaded" className="data-[state=active]:bg-[color:var(--rb-accent)] data-[state=active]:text-[color:var(--rb-void)]">
            <UploadIcon className="mr-1.5 h-3.5 w-3.5" /> Uploaded ({uploaded.length})
          </TabsTrigger>
          <TabsTrigger value="purchased" data-testid="tab-purchased" className="data-[state=active]:bg-[color:var(--rb-accent)] data-[state=active]:text-[color:var(--rb-void)]">
            <ShoppingBag className="mr-1.5 h-3.5 w-3.5" /> Purchased ({purchased.length})
          </TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived" className="data-[state=active]:bg-[color:var(--rb-accent)] data-[state=active]:text-[color:var(--rb-void)]">
            <ArchiveIcon className="mr-1.5 h-3.5 w-3.5" /> Archived ({archived.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="created" className="mt-6">
          {loading ? (
            <div className="flex justify-center py-16 text-[color:var(--rb-text-faint)]"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : created.length === 0 ? (
            <EmptyState label="You haven’t built any skills yet." cta="Build your first skill" onClick={() => navigate("/build")} />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {created.map((s) => (
                <CreatedCard key={s.id} skill={s} org={org} sharing={sharingId === s.id} onDownload={downloadOrBuy} onTogglePublic={togglePublic} onShare={shareToOrg} onEdit={setEditing} onEditContent={editContent} onView={viewDetail} onArchive={archive} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="uploaded" className="mt-6">
          {loading ? (
            <div className="flex justify-center py-16 text-[color:var(--rb-text-faint)]"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : uploaded.length === 0 ? (
            <EmptyState label="No uploads yet. Bring your own SKILL.md + helper files." cta="Upload a skill" onClick={() => navigate("/upload")} />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {uploaded.map((s) => (
                <UploadedCard key={s.id} skill={s} org={org} sharing={sharingId === s.id} onDownload={justDownload} onTogglePublic={togglePublic} onShare={shareToOrg} onEdit={setEditing} onEditContent={editContent} onView={viewDetail} onAppeal={appeal} onArchive={archive} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="purchased" className="mt-6">
          {loading ? (
            <div className="flex justify-center py-16 text-[color:var(--rb-text-faint)]"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : purchased.length === 0 ? (
            <EmptyState label="No purchases yet. Explore the marketplace to find skills." cta="Browse marketplace" onClick={() => navigate("/marketplace")} />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {purchased.map((s) => (
                <PurchasedCard key={s.id} skill={s} org={org} onDownload={justDownload} onReview={viewDetail} onFork={fork} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="archived" className="mt-6">
          {loading ? (
            <div className="flex justify-center py-16 text-[color:var(--rb-text-faint)]"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : archived.length === 0 ? (
            <EmptyState label="Nothing archived. Retired artifacts land here — unpublished, hidden, never deleted." cta="Back to Created" onClick={() => {}} />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {archived.map((s) => (
                <div key={s.id} onClick={cardClickGuard(() => viewDetail(s))}
                  className={`flex flex-col rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5 opacity-80 hover:opacity-100 ${CARD_CLICKABLE}`} data-testid="archived-skill-card">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">{s.category || "General"}</Badge>
                    <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]"><ArchiveIcon className="mr-1 h-3 w-3" /> Archived</Badge>
                  </div>
                  <h3 className="rb-mono mt-3 truncate text-[15px] font-semibold text-[color:var(--rb-text)]">{s.slug}</h3>
                  <p className="mt-1.5 line-clamp-2 text-sm text-[color:var(--rb-text-dim)]">{s.short_description || "—"}</p>
                  <div className="mt-4 flex gap-2 border-t border-[color:var(--rb-border)] pt-3">
                    <Button size="sm" variant="outline" onClick={() => unarchive(s)} data-testid="unarchive-button" className="flex-1 border-[color:var(--rb-border)]">
                      <ArchiveRestore className="mr-1 h-3.5 w-3.5" /> Unarchive
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <EditDialog skill={editing} open={!!editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={load} />
    </AppShell>
  );
}

function EmptyState({ label, cta, onClick }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[color:var(--rb-border)] py-16 text-center">
      <p className="text-sm text-[color:var(--rb-text-faint)]">{label}</p>
      {cta && <Button onClick={onClick} className="mt-4 rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">{cta}</Button>}
    </div>
  );
}
